
import { db } from './firebase';
import { doc, setDoc, getDoc, increment } from 'firebase/firestore';
import { getInstanceId } from './settings';
import type { LabelConfig } from '../types';
import { collectOrderStatKeys } from './statsKeys';

const STATS_COLLECTION = 'stats';

export const statsService = {
  // Called once an order has been saved successfully (not before: an order that fails is not counted).
  // One write per stats document, each on its own so that a problem with one never loses the others.
  logOrder: async (config: LabelConfig, knownIconIds?: ReadonlySet<string>) => {
    if (!db) return;

    try {
      const instanceId = getInstanceId();
      const keys = collectOrderStatKeys(config, knownIconIds);
      const documents: [string, string[]][] = [
        ['icons', keys.icons],
        ['fonts', keys.fonts],
        ['variants', keys.variants],
        ['themes', keys.themes],
      ];

      await Promise.all(documents.map(async ([docName, ids]) => {
        if (ids.length === 0) return;
        const counters = Object.fromEntries(ids.map(id => [id, increment(1)]));
        try {
          await setDoc(doc(db, 'settings', instanceId, STATS_COLLECTION, docName), counters, { merge: true });
        } catch (e) {
          console.warn(`[Stats] Could not log ${docName}:`, e);
        }
      }));
      console.log('[Stats] Order logged successfully');
    } catch (e) {
      console.error('[Stats] Failed to log order:', e);
    }
  },

  logTestStat: async () => {
      if (!db) throw new Error("Firebase DB unavailable");
      console.log('[Stats] Attempting test write...');
      const ref = doc(db, 'settings', getInstanceId(), STATS_COLLECTION, 'icons');
      await setDoc(ref, { 'test-sun': increment(1) }, { merge: true });
      console.log('[Stats] Test write successful');
  },

  getStats: async () => {
    if (!db) return { icons: {}, fonts: {}, variants: {}, themes: {} };

    const instanceId = getInstanceId();

    // Helper to safely fetch a doc, returning {} on failure/offline
    const safeFetch = async (docName: string) => {
        try {
            const d = await getDoc(doc(db, 'settings', instanceId, STATS_COLLECTION, docName));
            return d.exists() ? d.data() : {};
        } catch (e) {
            console.warn(`[Stats] Could not fetch ${docName} (offline or missing):`, e);
            return {};
        }
    };

    try {
      const [icons, fonts, variants, themes] = await Promise.all([
        safeFetch('icons'),
        safeFetch('fonts'),
        safeFetch('variants'),
        safeFetch('themes'),
      ]);

      return { icons, fonts, variants, themes };
    } catch (e) {
      console.error('[Stats] Failed to fetch stats:', e);
      // Return empty structure so UI doesn't crash
      return { icons: {}, fonts: {}, variants: {}, themes: {} };
    }
  }
};
